import { useState } from 'react';
import { donationRecords } from '@/mocks/community';
import { Link } from 'react-router-dom';

export default function DonationWall() {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'delivered' | 'shipping'>('all');

  const totalAmount = donationRecords.reduce((sum, r) => sum + r.amount, 0);
  const deliveredCount = donationRecords.filter(r => r.status === '已送达').length;

  const filtered = filter === 'all' ? donationRecords :
    filter === 'delivered' ? donationRecords.filter(r => r.status === '已送达') :
    donationRecords.filter(r => r.status === '配送中');

  return (
    <section className="py-16 bg-gradient-to-b from-rose-50/50 to-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-rose-100 rounded-full px-4 py-1.5 mb-4">
            <i className="ri-heart-fill text-rose-500 text-sm"></i>
            <span className="text-rose-700 text-sm font-semibold">爱心公示墙</span>
          </div>
          <h2 className="text-3xl font-black text-gray-900 mb-3">每一份爱心，都有记录</h2>
          <p className="text-gray-500 text-base max-w-2xl mx-auto">
            我们公开所有捐款记录，让每一位捐助者看到礼物送达的那一刻，让每一位受助学生感受到来自远方的温暖
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {[
            { label: '累计捐款', value: `¥${(totalAmount / 10000).toFixed(1)}万+`, icon: 'ri-money-cny-circle-line', color: 'text-rose-600' },
            { label: '爱心捐助者', value: '12,847人', icon: 'ri-user-heart-line', color: 'text-amber-600' },
            { label: '礼物已送达', value: `${deliveredCount}批次`, icon: 'ri-gift-line', color: 'text-green-600' },
            { label: '受益学生', value: '3,200+人', icon: 'ri-graduation-cap-line', color: 'text-blue-600' },
          ].map((stat) => (
            <div key={stat.label} className="bg-white rounded-2xl p-5 text-center border border-gray-100">
              <div className={`w-10 h-10 flex items-center justify-center mx-auto mb-2 bg-gray-50 rounded-full`}>
                <i className={`${stat.icon} text-xl ${stat.color}`}></i>
              </div>
              <p className={`text-xl font-black ${stat.color} mb-1`}>{stat.value}</p>
              <p className="text-gray-500 text-xs">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Filter */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <h3 className="text-xl font-black text-gray-900">捐赠记录公示</h3>
          <div className="flex gap-1 bg-white rounded-full p-1 border border-gray-100">
            {[
              { key: 'all', label: '全部' },
              { key: 'delivered', label: '已送达' },
              { key: 'shipping', label: '配送中' },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key as typeof filter)}
                className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  filter === f.key ? 'bg-rose-500 text-white' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Records */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
          {filtered.map((record) => (
            <div key={record.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:border-rose-200 transition-all">
              {/* Record Header */}
              <div className="p-5">
                <div className="flex items-start gap-4 mb-4">
                  {/* Donor */}
                  <div className="flex flex-col items-center gap-1 flex-shrink-0">
                    <img src={record.donorAvatar} alt={record.donor} className="w-10 h-10 rounded-full object-cover object-top border-2 border-rose-100" />
                    <span className="text-[10px] text-gray-400 text-center leading-tight max-w-[60px]">{record.donorCity}</span>
                  </div>

                  {/* Arrow */}
                  <div className="flex flex-col items-center justify-center flex-shrink-0 mt-2">
                    <div className="flex items-center gap-1">
                      <div className="w-8 h-0.5 bg-rose-200"></div>
                      <i className="ri-heart-fill text-rose-400 text-xs"></i>
                      <div className="w-8 h-0.5 bg-rose-200"></div>
                    </div>
                    <span className="text-[10px] text-rose-400 mt-0.5">爱心传递</span>
                  </div>

                  {/* Recipient */}
                  <div className="flex flex-col items-center gap-1 flex-shrink-0">
                    <img src={record.recipientAvatar} alt={record.recipient} className="w-10 h-10 rounded-full object-cover object-top border-2 border-green-100" />
                    <span className="text-[10px] text-gray-400 text-center leading-tight max-w-[60px]">受助学生</span>
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-bold text-gray-900 text-sm">{record.donor}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        record.status === '已送达' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {record.status}
                      </span>
                    </div>
                    <p className="text-gray-500 text-xs mb-1">捐助 → {record.recipient}</p>
                    <p className="text-gray-400 text-xs">{record.recipientRegion} · {record.recipientGrade}</p>
                    <p className="text-rose-600 font-bold text-sm mt-1">¥{record.amount}</p>
                  </div>
                </div>

                {/* Gifts */}
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {record.gifts.map((gift) => (
                    <span key={gift} className="text-xs bg-amber-50 text-amber-700 px-2.5 py-1 rounded-full border border-amber-100 flex items-center gap-1">
                      <i className="ri-gift-line text-xs"></i> {gift}
                    </span>
                  ))}
                </div>

                {/* Date */}
                <p className="text-gray-400 text-xs">{record.date}</p>
              </div>

              {/* Thank Note */}
              {record.thankNote && (
                <div
                  className="border-t border-gray-50 cursor-pointer"
                  onClick={() => setExpandedId(expandedId === record.id ? null : record.id)}
                >
                  <div className="px-5 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <i className="ri-message-3-line text-green-500 text-sm"></i>
                      <span className="text-xs font-semibold text-green-700">学生感谢留言</span>
                    </div>
                    <i className={`${expandedId === record.id ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-gray-400 text-sm`}></i>
                  </div>
                  {expandedId === record.id && (
                    <div className="px-5 pb-4">
                      <div className="bg-green-50 rounded-xl p-3">
                        <p className="text-green-800 text-sm leading-relaxed italic">&ldquo;{record.thankNote}&rdquo;</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center">
          <Link
            to="/donate"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-rose-500 text-white font-bold rounded-full cursor-pointer hover:bg-rose-600 transition-all whitespace-nowrap"
          >
            <i className="ri-heart-fill"></i>
            我也要捐款，帮助更多学生
          </Link>
          <p className="text-gray-400 text-xs mt-3">每一笔捐款都会公开记录，礼物送达后学生会留下感谢留言</p>
        </div>
      </div>
    </section>
  );
}
